<template>
  <v-container id="pdfContent">
    <div v-html="processedHtml"></div>
    <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg">
      <div class="border-thin rounded-t-lg bg-grey-darken-2 text-white text-h5 pa-2">Signature</div>
      <VueSignaturePad
        ref="signaturePad"
        height="400px"
        width="950px"
        :max-width="signatureOptions.maxWidth"
        :min-width="signatureOptions.minWidth"
        :options="{
          penColor: signatureOptions.penColor,
          backgroundColor: signatureOptions.backgroundColor,
        }"
        @endStroke="validateTerms" />

      <div v-if="!isSubmitted">
        <v-btn
          type="button"
          block
          class="bg-grey-darken-2 text-white pa-2 rounded-b-lg text-white"
          @click="handleClearCanvas('normal')">
          Clear
        </v-btn>
      </div>
    </v-sheet>
    <v-form v-model="isFormValid" @submit.prevent="onSubmit">
      <v-alert v-if="errors.signaturePad" type="error" class="mt-3">{{
        errors.signaturePad
      }}</v-alert>
      <div v-if="authStore.me?.isUnder18">
        <div v-if="!isSubmitted">
          <v-divider></v-divider>
          <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg">
            <div class="text-h5">Please have a parent or guardian complete the following.</div>
            <v-sheet class="border-opacity-25 overflow-hidden">
              <v-row>
                <v-col cols="12" md="6"
                  ><v-text-field
                    v-model="pgform.name"
                    label="Parent/Guardian Name"
                    :rules="
                      authStore.me?.isUnder18 ? [(v) => !!v || 'Name is required'] : []
                    "></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="pgform.address"
                    label="Parent/Guardian Address"
                    :rules="
                      authStore.me?.isUnder18 ? [(v) => !!v || 'Street Address is required'] : []
                    "></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="pgform.phone"
                    label="Parent/Guardian Phone"
                    :rules="phoneRules"></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="pgform.email"
                    label="Parent/Guardian Email"
                    :rules="emailRules"></v-text-field>
                </v-col>
              </v-row>
            </v-sheet>
          </v-sheet>
        </div>
        <div v-else>
          <p class="p2"><b>Parent/Guardian Name:</b> {{ pgform.name }}</p>
          <p class="p2"><b>Parent/Guardian Address:</b> {{ pgform.address }}</p>
          <p class="p2"><b>Parent/Guardian Phone:</b> {{ pgform.phone }}</p>
          <p class="p2"><b>Parent/Guardian Email:</b> {{ pgform.email }}</p>
        </div>
        <v-sheet class="border-opacity-25 overflow-hidden border-thin rounded-lg">
          <div class="border-thin rounded-t-lg bg-grey-darken-2 text-white text-h5 pa-2">
            Parent/Guardian Signature
          </div>

          <VueSignaturePad
            ref="pgSignaturePad"
            height="400px"
            width="950px"
            :max-width="signatureOptions.maxWidth"
            :min-width="signatureOptions.minWidth"
            :options="{
              penColor: signatureOptions.penColor,
              backgroundColor: signatureOptions.backgroundColor,
            }"
            @endStroke="validateTerms" />
          <v-alert v-if="errors.pgSignaturePad" type="error" class="mt-3">{{
            errors.pgSignaturePad
          }}</v-alert>
          <div v-if="!isSubmitted" class="absolute flex flex-col space-y-2 top-3 right-4">
            <v-btn
              type="button"
              block
              class="bg-grey-darken-2 text-white pa-2 rounded-b-lg text-white"
              @click="handleClearCanvas('pg')">
              Clear
            </v-btn>
          </div>
        </v-sheet>
      </div>
      <div v-if="!isSubmitted">
        <v-divider></v-divider>
        <v-btn
          block
          color="primary"
          outlined
          :disabled="(!isFormValid && authStore.me?.isUnder18) || !isSigned"
          type="submit">
          Submit Terms and Conditions
        </v-btn>
      </div>
      <div v-if="isSubmitted">
        <p class="p2"><b>Date:</b> {{ params.date }}</p>
        <p class="p2"><b>IPAddress:</b> {{ params.ipAddress }}</p>
        <p class="p2"><b>Browser:</b> {{ params.browser }}</p>
      </div>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import { useAuthStore, useTeamStore, useYearStore } from '@/stores';
import { VueSignaturePad } from '@selemondev/vue3-signature-pad';
import html2pdf from 'html2pdf.js';
import { formatDisplayDate, formatDateTime } from '@/helpers/dateTools';
import { IBaseFormFields, processedFormHTML, rules } from '@/helpers';
import { useFormTemplateStore } from '@/stores/modules';

interface ITermsFields extends IBaseFormFields {
  address: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  teamName: string | undefined;
  year: number | undefined;
  festStart: string | undefined;
  festEnd: string | undefined;
}

interface IParentGuardianFields {
  name: string | undefined;
  address: string | undefined;
  phone: string | undefined;
  email: string | undefined;
}

const emit = defineEmits(['termsFormCompleted']);
const authStore = useAuthStore();
const teamStore = useTeamStore();
const yearStore = useYearStore();
const formTemplateStore = useFormTemplateStore();
const templateString = ref(); // Retrieved from DB
const params = ref<ITermsFields>({
  date: '',
  ipAddress: '',
  browser: '',
  participantname: undefined,
  address: undefined,
  phone: undefined,
  email: undefined,
  teamName: undefined,
  year: undefined,
  festStart: undefined,
  festEnd: undefined,
});
const userIP = ref<string>('');
const signatureOptions = ref({
  penColor: 'rgb(0,0,0)',
  backgroundColor: 'rgb(255, 255, 255)',
  maxWidth: 2,
  minWidth: 2,
});
const errors = ref({ signaturePad: '', pgSignaturePad: '' });

const emailRules = computed(() => (authStore.me?.isUnder18 ? [rules.required, rules.email] : []));

const phoneRules = computed(() => (authStore.me?.isUnder18 ? [rules.required, rules.phone] : []));

const signaturePad = ref();
const pgSignaturePad = ref();
const isSubmitted = ref(false);
const pgform = ref<IParentGuardianFields>({
  name: undefined,
  address: undefined,
  phone: undefined,
  email: undefined,
});
const isFormValid = ref(false);
const isSigned = ref(false);

const processedHtml = computed(() => {
  return processedFormHTML(templateString.value, params.value);
});

function handleClearCanvas(value: string): void {
  if (value == 'normal') {
    signaturePad.value?.clearCanvas();
  } else {
    pgSignaturePad.value?.clearCanvas();
  }
  validateTerms();
  return;
}

const validateTerms = async () => {
  let isValid = true;
  if (!signaturePad.value || signaturePad.value?.isCanvasEmpty()) {
    isValid = false;
    errors.value.signaturePad = 'Please Sign';
  } else {
    errors.value.signaturePad = '';
  }

  if (authStore.me?.isUnder18 && pgSignaturePad.value?.isCanvasEmpty()) {
    isValid = false;
    errors.value.pgSignaturePad = 'Please Sign';
  } else {
    errors.value.pgSignaturePad = '';
  }
  isSigned.value = isValid;
  return isValid;
};

const onSubmit = async () => {
  const isValid = await validateTerms();
  if (!isValid) {
    return;
  } else {
    isSubmitted.value = true;
  }
  await nextTick();
  var pdfContent = document.getElementById('pdfContent');
  const pdfBlob = await html2pdf().from(pdfContent).outputPdf('blob');

  emit('termsFormCompleted', pdfBlob);
};

const getIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    userIP.value = data.ip;
  } catch (error) {
    console.error('Error fetching IP:', error);
  }
};

const loadTermsFormTemplate = async () => {
  const template = formTemplateStore.getDefaultTermsFormTemplate.data;
  if (template) {
    templateString.value = template.body;
  }
  const currentDate = new Date().toLocaleString();
  params.value = {
    date: formatDateTime(currentDate), //Get Current Date
    participantname: authStore.me?.displayName,
    phone: authStore.me?.phone_number,
    email: authStore.me?.email_address,
    address: authStore.me?.address,
    ipAddress: userIP.value,
    browser: navigator.userAgent,
    teamName: teamStore.myTeam ? teamStore.myTeam.name : undefined,
    year: yearStore.currentYear?.year,
    festStart: formatDisplayDate(yearStore.currentYear?.launch_date),
    festEnd: formatDisplayDate(yearStore.currentYear?.turn_deadline),
  };
};

onMounted(async () => {
  await getIP();
  await loadTermsFormTemplate();
});
</script>
